import { CSSProperties, FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Card } from '@beeline/design-system-react/extracted-styles';

import clsx from 'clsx';
import { BookingInfo } from 'src/api/bookingsView';
import { labelsByBookingObjectType } from 'src/pages/AdministratorPage/consts';
import { toBookingObjectType } from 'src/pages/AdministratorPage/utils';
import { RoutePath } from 'src/routes/types';
import { getIsDesktop } from 'src/store/agent/selectors';
import {
  getAreBookingsActual,
  getBookingsState,
} from 'src/store/bookings/selectors';
import { getUserPermissions } from 'src/store/user/selectors';
import { PlaceType, ProgressState } from 'src/types';
import { getDateTime } from 'src/utils/getDateTime';
import { isBookingAvailable } from 'src/utils/isBookingAvailable';
import { toAddress } from 'src/utils/toAddress';

import { useMyBookings } from '../MyBookingsProvider';

import classes from './BookingCard.module.scss';
import { DesktopBookingCard } from './DesktopBookingCard/DesktopBookingCard';
import { desktopBookingCardStyle } from './DesktopBookingCard/consts';
import { MobileBookingCard } from './MobileBookingCard/MobileBookingCard';
import {
  actualDeskMobileBookingCardStyle,
  actualMobileBookingCardStyle,
  historicalDeskMobileBookingCardStyle,
  historicalMobileBookingCardStyle,
} from './MobileBookingCard/consts';
import { BookingCardComponentProps } from './types';

const dateFormatter = new Intl.DateTimeFormat('ru', {
  day: 'numeric',
  month: 'long',
  weekday: 'short',
});

const timeFormatter = new Intl.DateTimeFormat('ru', {
  hour: 'numeric',
  minute: 'numeric',
});

export interface BookingCardProps {
  booking: BookingInfo;
}

const pathByPlaceType: Record<PlaceType, RoutePath> = {
  [PlaceType.Desk]: RoutePath.DeskBooking,
  [PlaceType.Room]: RoutePath.RoomBooking,
  [PlaceType.Parking]: RoutePath.Parking,
};

export const BookingCard: FC<BookingCardProps> = ({ booking }) => {
  const isDesktop = useSelector(getIsDesktop);
  const { unbook } = useMyBookings();
  const actual = useSelector(getAreBookingsActual);
  const { unbookings } = useSelector(getBookingsState);
  const permissions = useSelector(getUserPermissions)?.permissions;

  const title = `${labelsByBookingObjectType[toBookingObjectType(booking.placeType, booking.roomType)]} ${booking.placeTitle}`;

  const place = toAddress({
    town: booking.town.townName,
    building: booking.building.address,
    floor: booking.floor.title,
    skipFloorLabel: booking.placeType === PlaceType.Parking,
  });
  const bookedFrom = getDateTime({
    date: new Date(booking.bookedFrom),
    timezoneOffset: booking.town.timeZone,
  });
  const bookedTo = getDateTime({
    date: new Date(booking.bookedTo),
    timezoneOffset: booking.town.timeZone,
  });

  const date = dateFormatter.format(bookedFrom);
  const from = timeFormatter.format(bookedFrom);
  const to = timeFormatter.format(bookedTo);
  const time =
    booking.placeType === PlaceType.Desk ? undefined : `${from} — ${to}`;

  const handleUnbook = useCallback(
    async () => unbook(booking),
    [booking, unbook],
  );

  const link = useMemo((): string | undefined => {
    if (!isBookingAvailable({ ...booking, permissions })) return;

    const path = pathByPlaceType[booking.placeType];
    const searchParams = new URLSearchParams({
      searched: actual
        ? JSON.stringify({
            ...booking,
            bookedFrom: bookedFrom.toISOString(),
            bookedTo: bookedTo.toISOString(),
          })
        : JSON.stringify({ ...booking, bookedFrom: null, bookedTo: null }),
    });
    return `/${path}?${searchParams.toString()}`;
  }, [actual, bookedFrom, bookedTo, booking, permissions]);

  const buttonState = unbookings[booking.bookingId] as
    | ProgressState
    | undefined;

  const { card, bookingCardStyle } = useMemo(() => {
    const props: BookingCardComponentProps = {
      link,
      title,
      place,
      date,
      time,
      actual,
      onUnbook: handleUnbook,
      buttonState,
    };
    let style: CSSProperties = desktopBookingCardStyle;
    if (!isDesktop) {
      if (time) {
        style = actual
          ? actualMobileBookingCardStyle
          : historicalMobileBookingCardStyle;
      } else {
        style = actual
          ? actualDeskMobileBookingCardStyle
          : historicalDeskMobileBookingCardStyle;
      }
    }

    return {
      bookingCardStyle: style,
      card: isDesktop ? (
        <DesktopBookingCard {...props} />
      ) : (
        <MobileBookingCard {...props} />
      ),
    };
  }, [
    actual,
    buttonState,
    date,
    handleUnbook,
    isDesktop,
    link,
    place,
    time,
    title,
  ]);

  return (
    <Card
      border={actual ? 'default' : undefined}
      className={clsx(!actual && classes.historical)}
      style={bookingCardStyle}
    >
      {card}
    </Card>
  );
};
